import { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Typography,
  IconButton,
  Popover,
  Stack,
  TextField,
} from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";
import CreateActionIcon from "../../../ui/components/CreateActionIcon";
import {
  HOME_GLASS_BACKDROP,
  HOME_GLASS_BORDER,
} from "../../../pages/Home/constants/homeGlass";
import { getAllProjectStatuses } from "../../../core/services/projectStatus.service";
import { useAuth } from "../../../app/context/AuthContext";
import { MODULE_LIST } from "../../../constants/commons";

const THEME_ACCENT = "#D4A017";

const C = {
  text: "#f8fafc",
  muted: "rgba(255,255,255,0.55)",
  border: "rgba(255,255,255,0.18)",
  radiusMd: "10px",
};

export const createKeyDateEntry = (overrides = {}) => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  date: null,
  label: "",
  ...overrides,
});

export const getDefaultKeyDates = (defaultDate = null) => [
  createKeyDateEntry({ date: defaultDate, label: "" }),
];

const autocompletePaperSx = {
  bgcolor: "rgba(28, 29, 22, 0.96)",
  backdropFilter: HOME_GLASS_BACKDROP,
  border: HOME_GLASS_BORDER,
  color: "#fff",
  "& .MuiAutocomplete-option": {
    fontSize: 14,
    "&[aria-selected='true']": {
      bgcolor: `${THEME_ACCENT}33`,
    },
    "&.Mui-focused": {
      bgcolor: "rgba(255,255,255,0.06)",
    },
  },
};

const keyDateTextFieldSx = {
  input: {
    color: C.text,
    fontSize: 13,
    "&::placeholder": { color: "rgba(255,255,255,0.35)", opacity: 1 },
  },
  "& .MuiOutlinedInput-root": {
    height: 40,
    borderRadius: C.radiusMd,
    background: "rgba(255,255,255,0.07)",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
    "& fieldset": { borderColor: C.border },
    "&:hover fieldset": { borderColor: `${THEME_ACCENT}99` },
    "&.Mui-focused fieldset": {
      borderColor: THEME_ACCENT,
      borderWidth: 1,
    },
  },
};

const timelineDateButtonSx = {
  height: 40,
  border: `1px solid ${C.border}`,
  borderRadius: C.radiusMd,
  background: "rgba(255,255,255,0.07)",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
  px: 1.5,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  cursor: "pointer",
  font: "inherit",
  color: C.text,
  textAlign: "left",
  "&:hover": { borderColor: `${THEME_ACCENT}99` },
  "&:focus-visible": {
    outline: `2px solid ${THEME_ACCENT}66`,
    outlineOffset: 2,
  },
};

const WEEKDAYS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

function datesAreSameDay(a, b) {
  return (
    a instanceof Date &&
    b instanceof Date &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatTimelineDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "Due date";
  }
  return `Due: ${new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(date)}`;
}

function formatCalendarMonth(date) {
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
  }).format(date);
}

function buildCalendarCells(monthDate) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDayOffset = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [
    ...Array.from({ length: firstDayOffset }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  const trailingCells = (7 - (cells.length % 7)) % 7;
  return [...cells, ...Array.from({ length: trailingCells }, () => null)];
}

function getEntryDateValue(date) {
  if (date instanceof Date && !Number.isNaN(date.getTime())) {
    return date.getTime();
  }
  return Number.POSITIVE_INFINITY;
}

export function sortKeyDatesByDate(entries) {
  return entries
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => {
      const dateDiff =
        getEntryDateValue(a.entry.date) - getEntryDateValue(b.entry.date);
      if (dateDiff !== 0) return dateDiff;
      return a.index - b.index;
    })
    .map(({ entry }) => entry);
}

function getAvailableOptions(keyDates, entryId, options = []) {
  const usedLabels = new Set(
    keyDates
      .filter((entry) => entry.id !== entryId)
      .map((entry) => String(entry.label ?? "").trim().toLowerCase())
      .filter(Boolean),
  );

  return options
    .map((option) => option.label || option.value)
    .filter(Boolean)
    .filter((label) => !usedLabels.has(String(label).trim().toLowerCase()));
}

function TimelineCalendar({
  selectedDate,
  visibleMonth,
  onVisibleMonthChange,
  onSelectDate,
}) {
  const year = visibleMonth.getFullYear();
  const month = visibleMonth.getMonth();
  const cells = buildCalendarCells(visibleMonth);

  const changeMonth = (offset) => {
    onVisibleMonthChange(new Date(year, month + offset, 1));
  };

  return (
    <Box sx={{ width: 256, p: 1.4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.text }}>
          {formatCalendarMonth(visibleMonth)}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
          <IconButton
            size="small"
            onClick={() => changeMonth(-1)}
            aria-label="Previous month"
            sx={{
              p: 0.25,
              color: "rgba(255,255,255,0.7)",
              "&:hover": { background: "rgba(255,255,255,0.06)" },
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => changeMonth(1)}
            aria-label="Next month"
            sx={{
              p: 0.25,
              color: "rgba(255,255,255,0.7)",
              "&:hover": { background: "rgba(255,255,255,0.06)" },
            }}
          >
            <ChevronRightIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          rowGap: 0.75,
          columnGap: 0.25,
        }}
      >
        {WEEKDAYS.map((day) => (
          <Typography
            key={day}
            sx={{
              fontSize: 9,
              color: "rgba(255,255,255,0.35)",
              textAlign: "center",
              fontWeight: 600,
            }}
          >
            {day}
          </Typography>
        ))}
        {cells.map((day, index) => {
          if (!day) return <Box key={`empty-${index}`} sx={{ height: 28 }} />;

          const dayDate = new Date(year, month, day);
          const selected = datesAreSameDay(dayDate, selectedDate);

          return (
            <Box
              key={day}
              component="button"
              type="button"
              onClick={() => onSelectDate(dayDate)}
              sx={{
                width: 28,
                height: 28,
                mx: "auto",
                border: "none",
                borderRadius: "50%",
                bgcolor: selected ? `${THEME_ACCENT}44` : "transparent",
                color: selected ? THEME_ACCENT : C.text,
                cursor: "pointer",
                font: "inherit",
                fontSize: 13,
                fontWeight: selected ? 600 : 400,
                p: 0,
                "&:hover": {
                  bgcolor: selected ? `${THEME_ACCENT}44` : "rgba(255,255,255,0.08)",
                },
                "&:focus-visible": {
                  outline: `2px solid ${THEME_ACCENT}66`,
                  outlineOffset: 1,
                },
              }}
            >
              {day}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function TimelineRow({
  entry,
  availableOptions,
  calendarOpen,
  onOpenCalendar,
  onLabelChange,
  onRemove,
  canEdit
}) {
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.25,
        mb: 1,
        alignItems: "stretch",
        flexWrap: { xs: "wrap", sm: "nowrap" },
      }}
    >
      <Box
        component="button"
        type="button"
          onClick={(event) => {
            if(canEdit){
              onOpenCalendar(event);
            }
        }}
        aria-haspopup="dialog"
        aria-expanded={calendarOpen ? "true" : undefined}
        sx={{
          ...timelineDateButtonSx,
          flex: { xs: "1 1 100%", sm: 2 },
          minWidth: 0,
          ...(canEdit ? {} : { cursor: "not-allowed", opacity: 0.6 })
        }}
      >
        <Typography
          sx={{
            fontSize: 14,
            color: "rgba(255,255,255,0.55)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {formatTimelineDate(entry.date)}
        </Typography>
        <ArrowDropDownIcon sx={{ fontSize: 20, color: C.muted }} />
      </Box>
      <Box sx={{ flex: { xs: "1 1 100%", sm: 0.45 }, minWidth: 120 }}>
        <Autocomplete
          freeSolo
          fullWidth
          size="small"
          options={availableOptions}
          inputValue={entry.label}
          onInputChange={(_, value) => {
            if(canEdit){
              onLabelChange(value)
            }
          }}
          onChange={(_, value) =>{
            if(canEdit){
              onLabelChange(String(value ?? "").trim())
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              variant="outlined"
              placeholder="Select or enter key date"
              InputProps={params.InputProps}
              sx={{
                ...keyDateTextFieldSx,
                ...(canEdit ? {} : { cursor: "not-allowed", opacity: 0.6 })
              }}
            />
          )}
          slotProps={{
            paper: {
              sx: autocompletePaperSx,
              
            },
          }}
        />
      </Box>
      <IconButton
        size="small"
        onClick={onRemove}
        aria-label="Remove key date"
        sx={{
          display: canEdit ? "block" : "none",
          width: 40,
          height: 40,
          flexShrink: 0,
          alignSelf: "center",
          color: "rgba(255,255,255,0.35)",
          border: `1px solid ${C.border}`,
          borderRadius: C.radiusMd,
          "&:hover": {
            color: "#F87171",
            background: "rgba(248,113,113,0.08)",
            borderColor: "rgba(248,113,113,0.35)",
          },
        }}
      >
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
}

export default function TimelineKickoffFields({ keyDates, onKeyDatesChange, sx }) {
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  const [activeCalendar, setActiveCalendar] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);

   const { userPermissions } = useAuth();
    const timelineModule = Object.hasOwn(
      userPermissions,
      MODULE_LIST.PROJECT_TIMELINE
    )
      ? userPermissions[MODULE_LIST.PROJECT_TIMELINE]
      : false;
  
  let canEditTimeline = false;
  let canViewTimeline = false;

  if (timelineModule && timelineModule.view) {
    canViewTimeline = true;
    canEditTimeline = timelineModule.edit;

  }

  useEffect(() => {
    let cancelled = false;

    getAllProjectStatuses()
      .then((list) => {
        if (cancelled) return;
        setStatusOptions(
          list.map((item) => ({
            value: item.value,
            label: item.label || item.value,
          })),
        );
      })
      .catch(() => {
        if (!cancelled) setStatusOptions([]);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const calendarOpen = Boolean(activeCalendar?.anchorEl);
  const activeEntry =
    activeCalendar != null
      ? keyDates.find((entry) => entry.id === activeCalendar.entryId)
      : null;

  const updateEntry = (entryId, patch) => {
    const nextEntries = keyDates.map((entry) =>
      entry.id === entryId ? { ...entry, ...patch } : entry,
    );
    onKeyDatesChange(
      patch.date != null ? sortKeyDatesByDate(nextEntries) : nextEntries,
    );
  };

  const handleOpenCalendar = (entryId, event) => {
    const entry = keyDates.find((item) => item.id === entryId);
    const base =
      entry?.date instanceof Date && !Number.isNaN(entry.date.getTime())
        ? entry.date
        : new Date();
    setCalendarMonth(new Date(base.getFullYear(), base.getMonth(), 1));
    setActiveCalendar({ entryId, anchorEl: event.currentTarget });
  };

  const handleSelectTimelineDate = (nextDate) => {
    if (!activeCalendar) return;
    const nextEntries = keyDates.map((entry) =>
      entry.id === activeCalendar.entryId ? { ...entry, date: nextDate } : entry,
    );
    onKeyDatesChange(sortKeyDatesByDate(nextEntries));
    setCalendarMonth(new Date(nextDate.getFullYear(), nextDate.getMonth(), 1));
    setActiveCalendar(null);
  };

  const handleAddKeyDate = () => {
    onKeyDatesChange(sortKeyDatesByDate([...keyDates, createKeyDateEntry()]));
  };

  const handleRemoveEntry = (entryId) => {
    if (activeCalendar?.entryId === entryId) {
      setActiveCalendar(null);
    }

    if (keyDates.length === 1) {
      onKeyDatesChange([createKeyDateEntry()]);
      return;
    }

    onKeyDatesChange(
      sortKeyDatesByDate(keyDates.filter((entry) => entry.id !== entryId)),
    );
  };

  const sortedKeyDates = sortKeyDatesByDate(keyDates);

  return (
    <>
      <Box sx={{ width: "100%", ...sx, display: canViewTimeline ? "block" : "none" }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <CalendarTodayOutlinedIcon
            sx={{ fontSize: 17, color: "rgba(255,255,255,0.55)" }}
          />
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, letterSpacing: 0.02 }}>
            Timeline
          </Typography>
        </Stack>
        <Box sx={{ width: "100%" }}>
          {sortedKeyDates.map((entry) => (
            <TimelineRow
              key={entry.id}
              entry={entry}
              availableOptions={getAvailableOptions(
                keyDates,
                entry.id,
                statusOptions,
              )}
              calendarOpen={calendarOpen && activeCalendar?.entryId === entry.id}
              onOpenCalendar={(event) => handleOpenCalendar(entry.id, event)}
              onLabelChange={(label) => updateEntry(entry.id, { label })}
              onRemove={() => handleRemoveEntry(entry.id)}
              canEdit={canEditTimeline}
            />
          ))}
          <Typography
            component="button"
            type="button"
            onClick={handleAddKeyDate}
            sx={{
              display: canEditTimeline ? "inline-flex" : "none",
              alignItems: "center",
              gap: 0.6,
              width: "100%",
              textAlign: "left",
              border: "none",
              background: "none",
              cursor: "pointer",
              font: "inherit",
              fontSize: 12,
              color: C.muted,
              p: 0,
              "&:hover": { color: THEME_ACCENT },
            }}
          >
            <CreateActionIcon sx={{ fontSize: 17, flexShrink: 0 }} />
            key date
          </Typography>
        </Box>
      </Box>
      <Popover
        open={calendarOpen}
        anchorEl={activeCalendar?.anchorEl ?? null}
        onClose={() => setActiveCalendar(null)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{
          sx: {
            borderRadius: C.radiusMd,
            boxShadow: "0 16px 42px rgba(0,0,0,0.28)",
            border: HOME_GLASS_BORDER,
            background:
              "linear-gradient(145deg, rgba(64, 64, 53, 0.92), rgba(28, 29, 22, 0.96))",
            backdropFilter: HOME_GLASS_BACKDROP,
            WebkitBackdropFilter: HOME_GLASS_BACKDROP,
            color: "#fff",
            mt: -0.75,
          },
        }}
      >
        <TimelineCalendar
          selectedDate={activeEntry?.date ?? null}
          visibleMonth={calendarMonth}
          onVisibleMonthChange={setCalendarMonth}
          onSelectDate={handleSelectTimelineDate}
        />
      </Popover>
    </>
  );
}
